// Premium units in a match (2026-10-25). A match can hold 288 soldiers, too many for a skinned mesh and an animation
// mixer each, so each unit is baked once at load: every move is played on the real model (its props, its siege rig)
// and sampled at FPS, and every moving part's matrix goes into one float texture, a row per frame. A skinned mesh's
// bones are parts, and so is every rigid mesh (the yari, the cart's wheels, the bolt), so one format covers soldiers,
// props and engines. In the match each unit is an instanced mesh per material; the vertex shader reads its row
// (aRow, set per soldier each frame) and blends up to four part matrices, and the team color comes per soldier (aTeam)
// through teamtint.ts's repaint.
import * as THREE from 'three';
import { teamTintGLSL } from './teamtint';
import { enamel } from './look';

export const FPS = 30;

/** `stride`: for a horse's gait, the ground one cycle covers, in the unit's own units (mount.ts strideOf). */
export interface BakedMove { start: number; frames: number; duration: number; stride?: number }
export interface Baked {
  parts: { geometry: THREE.BufferGeometry; material: THREE.Material }[];
  texture: THREE.DataTexture;
  /** The sun's depth pass for this unit, posed from the same rows (heroes cast a real shadow; look.ts). */
  depth: THREE.MeshDepthMaterial;
  moves: Record<string, BakedMove>;
  /** Body height (feet to skull top, premium.ts proportion), in the unit's own units; bake() sets the whole height. */
  height: number;
}

/** Poses the unit at time t of a move. */
export type Drive = (move: string, t: number) => void;

type Slot = { mesh: THREE.Mesh; bone?: THREE.Bone; inverse?: THREE.Matrix4 };

/** Bake a loaded unit. `moves` maps each move to its length in seconds; `drive` poses it. Hidden parts bake as a
 *  zero matrix, so a part that comes and goes (the reloaded bolt) does so in the match too. */
export function bake(root: THREE.Object3D, moves: Record<string, number>, drive: Drive): Baked {
  root.updateMatrixWorld(true);
  const height = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3()).y / root.getWorldScale(new THREE.Vector3()).y;
  const slots: Slot[] = [], groups = new Map<string, { mat: THREE.MeshStandardMaterial; geos: THREE.BufferGeometry[] }>();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const sk = (mesh as THREE.SkinnedMesh).isSkinnedMesh ? (mesh as THREE.SkinnedMesh).skeleton : null, base = slots.length;
    if (sk) sk.bones.forEach((bone, k) => slots.push({ mesh, bone, inverse: sk.boneInverses[k] }));
    else slots.push({ mesh });
    for (const [geo, mat] of split(mesh)) {
      const g = new THREE.BufferGeometry(), n = geo.attributes.position.count;
      g.setAttribute('position', geo.attributes.position);
      g.setAttribute('normal', geo.attributes.normal ?? new THREE.Float32BufferAttribute(new Float32Array(n * 3), 3));
      g.setAttribute('uv', geo.attributes.uv ?? new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
      const idx = new Float32Array(n * 4), w = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) {
        if (sk) for (let j = 0; j < 4; j++) { idx[i * 4 + j] = base + geo.attributes.skinIndex.getComponent(i, j); w[i * 4 + j] = geo.attributes.skinWeight.getComponent(i, j); }
        else { idx[i * 4] = base; w[i * 4] = 1; }
      }
      g.setAttribute('skinIndex', new THREE.Float32BufferAttribute(idx, 4));
      g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(w, 4));
      const m = mat as THREE.MeshStandardMaterial, key = m.map ? m.map.uuid : 'paint';
      if (!m.map) { // painted: the file's vertex colors (the match files, pack_lods.ts) times the material color
        const c = m.color ?? new THREE.Color(1, 1, 1), vc = geo.attributes.color, col = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) for (let j = 0; j < 3; j++) col[i * 3 + j] = (vc ? vc.getComponent(i, j) : 1) * [c.r, c.g, c.b][j];
        g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      }
      g.setIndex(geo.index ? geo.index.clone() : [...Array(n).keys()]);
      if (!groups.has(key)) groups.set(key, { mat: m, geos: [] });
      groups.get(key)!.geos.push(g);
    }
  });

  const total = Object.values(moves).reduce((n, d) => n + Math.max(1, Math.round(d * FPS)), 0), W = slots.length * 4;
  if (W > 4096) throw new Error(`a unit with ${slots.length} parts is too wide to bake`);
  const data = new Float32Array(W * total * 4), out: Record<string, BakedMove> = {};
  const rootInv = new THREE.Matrix4(), m = new THREE.Matrix4(), zero = new THREE.Matrix4().makeScale(0, 0, 0);
  let row = 0;
  for (const [name, duration] of Object.entries(moves)) {
    const frames = Math.max(1, Math.round(duration * FPS));
    out[name] = { start: row, frames, duration };
    for (let f = 0; f < frames; f++, row++) {
      drive(name, f / FPS);
      root.updateMatrixWorld(true);
      rootInv.copy(root.matrixWorld).invert();
      slots.forEach((s, k) => {
        if (!shown(s.mesh)) m.copy(zero);
        else if (s.bone) {
          const sm = s.mesh as THREE.SkinnedMesh; // root from mesh-bind space: the same product three's skinning uses
          m.multiplyMatrices(rootInv, sm.matrixWorld).multiply(sm.bindMatrixInverse).multiply(s.bone.matrixWorld).multiply(s.inverse!).multiply(sm.bindMatrix);
        } else m.multiplyMatrices(rootInv, s.mesh.matrixWorld);
        data.set(m.elements, (row * W + k * 4) * 4);
      });
    }
  }
  const texture = new THREE.DataTexture(data, W, total, THREE.RGBAFormat, THREE.FloatType);
  texture.needsUpdate = true;
  const parts = [...groups.values()].map(({ mat, geos }) => ({ geometry: merge(geos), material: bakedMaterial(mat, texture) }));
  return { parts, texture, depth: bakedDepthMaterial(texture), moves: out, height };
}

/** A mesh's (geometry, material) pairs: one per material group, or the whole mesh. */
function split(mesh: THREE.Mesh): [THREE.BufferGeometry, THREE.Material][] {
  const geo = mesh.geometry, mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  if (!geo.groups.length || mats.length === 1) return [[geo, mats[0]]];
  return geo.groups.map((gr) => {
    const g = geo.clone(), idx = geo.index!.array.slice(gr.start, gr.start + gr.count);
    g.setIndex([...idx]); g.clearGroups();
    return [g, mats[gr.materialIndex ?? 0]] as [THREE.BufferGeometry, THREE.Material];
  });
}

function shown(o: THREE.Object3D | null): boolean {
  for (; o; o = o.parent) if (!o.visible) return false;
  return true;
}

function merge(geos: THREE.BufferGeometry[]) {
  const out = new THREE.BufferGeometry(), names = Object.keys(geos[0].attributes);
  let n = 0;
  const index: number[] = [];
  for (const g of geos) { for (const i of g.index!.array) index.push(i + n); n += g.attributes.position.count; }
  for (const name of names) {
    const size = geos[0].attributes[name].itemSize, arr = new Float32Array(n * size);
    let at = 0;
    for (const g of geos) { const a = g.attributes[name]; for (let i = 0; i < a.count; i++) for (let j = 0; j < size; j++) arr[at++] = a.getComponent(i, j); }
    out.setAttribute(name, new THREE.Float32BufferAttribute(arr, size));
  }
  out.setIndex(index);
  return out;
}

/** The bake's vertex code: the soldier's row of part matrices, four blended per vertex (BAKE_POSE after begin_vertex). */
const BAKE_PARS = `
uniform highp sampler2D uBake;
attribute vec4 skinIndex; attribute vec4 skinWeight; attribute float aRow;
mat4 bakedPart(float part) {
  int x = int(part) * 4, y = int(aRow);
  return mat4(texelFetch(uBake, ivec2(x, y), 0), texelFetch(uBake, ivec2(x + 1, y), 0), texelFetch(uBake, ivec2(x + 2, y), 0), texelFetch(uBake, ivec2(x + 3, y), 0));
}
mat4 bakedPose() {
  return bakedPart(skinIndex.x) * skinWeight.x + bakedPart(skinIndex.y) * skinWeight.y
       + bakedPart(skinIndex.z) * skinWeight.z + bakedPart(skinIndex.w) * skinWeight.w;
}`;

/** Glossy enamel over the unit's maps (color, normal, roughness and metal, scripts/lib/atlas.ts) or a code prop's
 *  painted color: a physical material with a clear coat, so the paint shines like a cast figure's gloss and takes
 *  scene.environment when the stage has one. Skinned from the bake texture, tinted per soldier. 2026-10-26: Phong and
 *  vertex colors before, which read flat and could not mirror anything. */
export const GLOSS = { clearcoat: 1, clearcoatRoughness: 0.12 };
export const PAINT = { roughness: 0.45, metalness: 0 };
function bakedMaterial(src: THREE.MeshStandardMaterial, texture: THREE.DataTexture) {
  // the coat follows the sculpt too: three gives it the flat mesh normal unless it has its own map (Flash). Painted
  // props share one draw, so one finish (the first prop's wood made the yari's steel wood, Flash): PAINT.
  const mat = new THREE.MeshPhysicalMaterial({
    map: src.map ?? null, normalMap: src.normalMap ?? null, normalScale: src.normalScale?.clone(),
    clearcoatNormalMap: src.normalMap ?? null, clearcoatNormalScale: src.normalScale?.clone(),
    roughnessMap: src.roughnessMap ?? null, metalnessMap: src.metalnessMap ?? null,
    ...(src.map ? { roughness: src.roughness ?? 1, metalness: src.metalness ?? 0 } : PAINT), vertexColors: !src.map, ...GLOSS,
  });
  mat.userData.uBake = texture; // its shader's texture, kept where src/site/free3d.ts freeScene finds it
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uBake = { value: texture };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>${BAKE_PARS}
attribute vec3 aTeam;
varying vec3 vTeam;`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
  mat4 baked = bakedPose();
  objectNormal = mat3(baked) * objectNormal;
  vTeam = aTeam;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
  transformed = (baked * vec4(transformed, 1.0)).xyz;`);
    // after color_fragment, not map_fragment: painted parts (the great bow's blue band) get their color there
    sh.fragmentShader = 'varying vec3 vTeam;\n' + sh.fragmentShader.replace('#include <color_fragment>', '#include <color_fragment>' + teamTintGLSL('vTeam'));
    enamel(sh);
  };
  mat.customProgramCacheKey = () => `baked-${!!src.map}-${!!src.normalMap}`;
  return mat;
}

/** The depth pass for the sun's shadow, posed like the soldier (three's own depth material would draw the rest pose). */
function bakedDepthMaterial(texture: THREE.DataTexture) {
  const mat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  mat.userData.uBake = texture;
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uBake = { value: texture };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>' + BAKE_PARS)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  transformed = (bakedPose() * vec4(transformed, 1.0)).xyz;');
  };
  mat.customProgramCacheKey = () => 'baked-depth';
  return mat;
}

/** Marks only the first n instances of a per-instance buffer for upload. 2026-10-25: re-sending every buffer at the
 *  match's full cap each frame cost the 3D armies 14 fps of 50 on the desktop GPU (scripts/fps.ts); sending the
 *  soldiers drawn closed the gap. */
export function uploadFirst(a: THREE.BufferAttribute, n: number) {
  a.clearUpdateRanges();
  if (!n) return;
  a.addUpdateRange(0, n * a.itemSize);
  a.needsUpdate = true;
}

/** One unit drawn for many soldiers: an instanced mesh per baked part, all sharing the per-soldier buffers. */
export class BakedArmy {
  readonly meshes: THREE.InstancedMesh[];
  private matrix: THREE.InstancedBufferAttribute;
  private row: THREE.InstancedBufferAttribute;
  private team: THREE.InstancedBufferAttribute;
  n = 0;

  /** `cast`: these soldiers cast the sun's shadow (heroes; everyone else has a blob under him, main.ts). */
  constructor(readonly unit: Baked, cap: number, cast = false) {
    this.matrix = new THREE.InstancedBufferAttribute(new Float32Array(cap * 16), 16);
    this.row = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    this.team = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    for (const a of [this.matrix, this.row, this.team]) a.setUsage(THREE.DynamicDrawUsage);
    this.meshes = unit.parts.map(({ geometry, material }) => {
      // the per-soldier buffers belong to this army; the vertex data is shared, the same attributes (one GPU buffer
      // each), not a clone's copies (2026-10-26, Astra: geometry.clone() copied every vertex for every army and its heroes)
      const g = new THREE.BufferGeometry();
      for (const [k, a] of Object.entries(geometry.attributes)) g.setAttribute(k, a);
      g.setIndex(geometry.index); g.groups = geometry.groups; g.boundingSphere = geometry.boundingSphere; g.boundingBox = geometry.boundingBox;
      g.setAttribute('aRow', this.row); g.setAttribute('aTeam', this.team);
      const mesh = new THREE.InstancedMesh(g, material, cap);
      mesh.instanceMatrix = this.matrix; mesh.frustumCulled = false; mesh.count = 0;
      mesh.receiveShadow = true;
      if (cast) { mesh.castShadow = true; mesh.customDepthMaterial = unit.depth; }
      return mesh;
    });
  }

  /** The row of `move` at t seconds into it, looping; with loop false, outside it it stands (walk's first frame).
   *  A unit without the move stands too. */
  rowAt(move: string, t: number, loop = true) {
    const mv = this.unit.moves[move];
    if (!mv || (!loop && (t < 0 || t >= mv.duration))) return (this.unit.moves.walk ?? Object.values(this.unit.moves)[0]).start;
    const f = Math.floor(((((t / mv.duration) % 1) + 1) % 1) * mv.frames);
    return mv.start + Math.min(mv.frames - 1, f);
  }

  /** How many soldiers the buffers hold. */
  get cap() { return this.row.count; }

  set(k: number, matrix: THREE.Matrix4, row: number, team: THREE.Color) {
    matrix.toArray(this.matrix.array, k * 16);
    (this.row.array as Float32Array)[k] = row;
    team.toArray(this.team.array, k * 3);
  }

  commit() {
    for (const m of this.meshes) m.count = this.n;
    for (const a of [this.matrix, this.row, this.team]) uploadFirst(a, this.n);
  }
}
